import { model as i18n } from '!/i18n'
import { model as router } from '!/router'
import { model as cartModel } from '$/cart'
import { T } from '@/helpers'
import { api } from '@setplex/tria-api'
import { combine, createEvent, createStore, sample, type Store } from 'effector'
import { not, or, reset } from 'patronum'
import { model as subscriber } from '~/entities/subscriber'
import { type PaymentChannel } from '~/features/payment/interfaces'
import * as pinLockFeature from '~/features/pin-lock'

export const resetErrors = createEvent()

export const openPaymentPopupWithType = createEvent<PaymentChannel | null>()
export const tryClosePaymentPopup = createEvent()
export const closePaymentPopup = createEvent()
export const closePaymentPopupWithType = createEvent<PaymentChannel | null>()

export const $isAnyPaymentPopupOpened = createStore<boolean>(false)
export const anyPaymentInitiated = createEvent()

/********** PopUp **********/
sample({
  clock: $isAnyPaymentPopupOpened,
  target: pinLockFeature.closePinLockPopup,
})

// close payment popup on location change
sample({
  clock: router.$location,
  target: closePaymentPopup,
})
/********** PopUp **********/

/***********  Steps ***********/

export const setSpecialRequestIsPending = createEvent()
// Gravy form call state and authorize call state
export const $specialRequestIsPending = createStore<boolean>(false).on(
  setSpecialRequestIsPending,
  (_, payload) => payload
)

export const $genericRequestIsPending: Store<boolean> = or(
  api.purchases.getListPaymentChannelsFx.pending, // GET /v3/payment/channels
  api.cart.postItemToCartFx.pending, // POST /v3/cart/items
  api.creditCards.getSavedCreditCardsFx.pending, // /v3/creditcards
  api.cart.setPaymentDataCartFx.pending, // /v3/cart/properties
  api.cart.checkoutSelectedContentItemFx.pending, // POST /v3/cart/checkout
  api.cart.confirmCartPurchaseFx.pending, // POST /v3/cart/confirm
  api.purchases.initializePurchaseProcessFx.pending // /v3/purchases/payment
)

export const $paymentStepRequestIsPending: Store<boolean> = or(
  $specialRequestIsPending,
  $genericRequestIsPending
)
/***********  Steps ***********/

export const $purchaseProcessIsFailed = createStore<boolean>(false)

export const $paymentInfo = createStore<{ total?: number; currency?: string }>(
  {}
).on(cartModel.postItemToCartFx.doneData, (_, { total, currency }) => ({
  total,
  currency,
}))
export const $paymentSumTitle: Store<string> = combine(
  i18n.$t,
  $paymentInfo,
  ({ t }, { total, currency }) =>
    t('total_amount_text', {
      amount: total,
      currency,
    })
)

export const $invoiceDescription: Store<string> = combine(
  i18n.$t,
  subscriber.$subscriber,
  ({ t }, subscriberInfo) => {
    const email = subscriberInfo?.email
    if (!email) return ''
    return t('invoice_to_email', {
      email,
    })
  }
)

/************* Steps ***************/

export const resetStep = createEvent()

export const goBackToPreviousStep = createEvent()

sample({
  clock: closePaymentPopup,
  target: resetStep,
})

sample({
  clock: api.cart.setPaymentDataCartFx.done,
  target: api.cart.checkoutSelectedContentItemFx,
})

sample({
  clock: api.cart.checkoutSelectedContentItemFx.done,
  target: api.cart.confirmCartPurchaseFx,
})
/************* Steps ***************/

sample({
  clock: api.purchases.initializePurchaseProcessFx.fail,
  fn: T,
  target: $purchaseProcessIsFailed,
})

reset({
  clock: [closePaymentPopup],
  target: $purchaseProcessIsFailed,
})

// allow or disallow to close payment popup
sample({
  clock: tryClosePaymentPopup,
  filter: not($paymentStepRequestIsPending),
  target: closePaymentPopup,
})
